import { CircularProgress } from "@mui/material";
import ChipView, { ChipViewStyle } from "../common/ChipView";
import { formatNumberWithCommas } from "../common/strings";
import LearningHistory from "./LearningHistory";
import styles from "./LearningHistoryCell.module.css";
import { getLearningRangeText } from "./learning";
import { LearningHistoryRange } from "./learningHistoryRange";
import { ClipLoader } from "react-spinners";
import { useTranslation } from "react-i18next";

interface LearningHistoryCellProps {
  learningHistory: LearningHistory;
  learningHistoryRange: LearningHistoryRange;
  handleOnClick: (item: LearningHistory) => void;
}

export default function LearningHistoryCell(props: LearningHistoryCellProps) {
  const { t } = useTranslation();

  const title = getLearningRangeText(props.learningHistoryRange);

  const correctTermsCount = formatNumberWithCommas(
    props.learningHistory.correctTermsCount
  );
  const incorrectTermsCount = formatNumberWithCommas(
    props.learningHistory.incorrectTermsCount
  );

  const correctString = t("CorrectAnswer") + " " + correctTermsCount;
  const incorrectString = t("IncorrectAnswer") + " " + incorrectTermsCount;

  const totalLearnedTermsCount =
    props.learningHistory.correctTermsCount +
    props.learningHistory.incorrectTermsCount;

  const correctRatio =
    (props.learningHistory.correctTermsCount / totalLearnedTermsCount) * 100;
  const safeCorrectRatio = isNaN(correctRatio) ? 0 : Math.round(correctRatio);

  function isVisibleIncorrectChip(): boolean {
    return props.learningHistory.incorrectTermsCount !== 0;
  }

  return (
    <>
      {props.learningHistory.correctTermsCount < 0 && (
        <div className={styles.loadingContainer}>
          <ClipLoader
            loading={true}
            size={32}
            color="var(--icon-active-color)"
          />
        </div>
      )}
      {props.learningHistory.correctTermsCount >= 0 && (
        <div
          className={styles.container}
          onClick={() => {
            props.handleOnClick(props.learningHistory);
          }}
        >
          <div className={styles.leftContainer}>
            <div className={styles.contentContainer}>
              <span className={styles.titleText}>{title}</span>
              <span className={styles.termsLearnedCountValueText}>
                {correctTermsCount}
              </span>
            </div>
            <div className={styles.correctAndIncorrectContainer}>
              <ChipView title={correctString} style={ChipViewStyle.correct} />
              {isVisibleIncorrectChip() && (
                <ChipView
                  title={incorrectString}
                  style={ChipViewStyle.incorrect}
                />
              )}
            </div>
          </div>
          <div className={styles.rightContainer}>
            <CircularProgress
              variant="determinate"
              sx={{
                color: "var(--control-active-color)",
              }}
              size={40}
              thickness={4}
              value={safeCorrectRatio}
            />
            <span className={styles.percentageText}>
              {safeCorrectRatio + "%"}
            </span>
          </div>
        </div>
      )}
    </>
  );
}
